import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { ExclamationCircleIcon, MailIcon, PhoneIcon } from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftCouponByIdDataAtomFamily,
  giftCouponByIdStatusAtomFamily,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  requestReselectCancel,
  reselectCoupon,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponByIdHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponForPublic,
  GiftCouponRedeemStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import {
  GIFT_SUPPORT_PHONE,
  GIFT_SUPPORT_PHONE_TEL,
} from '@shared/misc/giftSupportPhone';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { AxiosError } from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  formatKoreanDate,
  getNextCouponSendSlot,
} from './giftCouponRedeem/giftCouponRedeem.utils';
import SendScheduleCard from './giftCouponRedeem/SendScheduleCard';

/**
 * 하단 문의 안내 — 품절은 설명이 필요할 수 있어 전화번호를 함께 두되,
 * 강조하지 않고 기존 문의 표기 수준으로만 노출한다.
 */
const ContactLine: FC<{ className?: string }> = ({ className = 'mt-6' }) => (
  <div
    className={`flex items-center justify-center gap-3 text-xs text-stone-500 ${className}`}
  >
    <a href={`tel:${GIFT_SUPPORT_PHONE_TEL}`} className="inline-flex items-center gap-1">
      <PhoneIcon className="h-3 w-3" />
      <span>문의 {GIFT_SUPPORT_PHONE}</span>
    </a>
    <span className="text-stone-300">|</span>
    <a href="mailto:smartnanumi@kakao.com" className="inline-flex items-center gap-1">
      <MailIcon className="h-3 w-3" />
      <span>smartnanumi@kakao.com</span>
    </a>
  </div>
);

/** 안내 화면 공통 껍데기 — 재선택 페이지의 모든 종료 상태에서 재사용 */
const NoticeScreen: FC<{
  tone?: 'success' | 'info' | 'error';
  title: string;
  message?: string;
  children?: React.ReactNode;
}> = ({ tone = 'info', title, message, children }) => (
  <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white px-4 py-8">
    <div className="mx-auto max-w-md">
      <div
        className={`rounded-xl p-6 text-center ${
          tone === 'error'
            ? 'border border-red-100 bg-red-50'
            : 'border border-stone-200 bg-stone-50'
        }`}
      >
        {tone === 'success' ? (
          <CheckCircleSolidIcon className="mx-auto h-10 w-10 text-stone-600" />
        ) : (
          <ExclamationCircleIcon
            className={`mx-auto h-10 w-10 ${
              tone === 'error' ? 'text-red-400' : 'text-stone-500'
            }`}
          />
        )}
        <h2 className="mt-3 break-keep text-lg font-medium text-stone-800">{title}</h2>
        {message && (
          <p className="mt-1 whitespace-pre-line break-keep text-sm text-stone-500">
            {message}
          </p>
        )}
      </div>
      {children}
      <ContactLine />
    </div>
  </div>
);

/**
 * 관리자 미리보기용 샘플 쿠폰 (`/coupon/preview/reselect?secretKey=...`).
 * 실데이터 없이 화면 구성만 확인하는 용도라 화면에 쓰는 필드만 채운다.
 * 이미지 URL은 넣지 않는다 — 실서비스에서는 상품 이미지가 함께 표시된다.
 */
const PREVIEW_COUPON = {
  _id: 'preview',
  code: 'PREVIEW',
  redeemStatus: GiftCouponRedeemStatus.ReselectRequired,
  giftItem: {
    _id: 'preview-item',
    name: '답례품 교환권',
    giftItemInfo: {
      _id: 'preview-item-info',
      name: '답례품 교환권',
      isSelectable: true,
      choiceUnits: [
        { _id: 'unit-1', name: '아메리카노 2잔', giftItemBrand: { name: '스타벅스' } },
        {
          _id: 'unit-2',
          name: '카페라떼 + 조각케이크',
          giftItemBrand: { name: '투썸플레이스' },
        },
        { _id: 'unit-3', name: '8,000원 금액권', giftItemBrand: { name: 'GS25' } },
      ],
    },
  },
  giftOrder: { _id: 'preview-order', receiverName: '김상주' },
  selectedChoiceUnit: { _id: 'unit-soldout', name: '8,500원 음료 교환권' },
  reselect: {
    status: 'Notified',
    noticeSentAt: new Date(),
    noticeMessage: [
      '[답례품 재선택 안내]',
      '신청해 주신 상품이 교환처의 판매 종료로 품절되었습니다.',
      '',
      '번거롭게 해 드려 죄송합니다.',
      '아래에서 원하시는 상품으로 다시 선택해 주시면',
      '바로 준비해 드리겠습니다.',
    ].join('\n'),
    soldOutChoiceUnit: { _id: 'unit-soldout', name: '8,500원 음료 교환권' },
    deadlineAt: new Date(Date.now() + 14 * 86_400_000),
    noticeCount: 1,
  },
  expiresAt: new Date(Date.now() + 200 * 86_400_000),
} as unknown as GiftCouponForPublic;

/**
 * 품절 재선택 페이지 (수령자용) — `coupon/:couponId/reselect`
 *
 * 교환 신청한 상품이 품절이라 관리자가 안내를 보낸 건에 대해, 수령자가 다른 상품을 다시
 * 고르거나 선물을 거절(구매자 전액 환불)할 수 있다.
 *
 * - **번호 인증·서명을 다시 받지 않는다.** 이미 인증·서명하고 신청한 본인이며, 링크가
 *   couponId 기반(보안레벨 2)이라 추측 진입이 사실상 불가하다.
 * - **금액을 노출하지 않는다.** 수령자는 결제 당사자가 아니라 상품가를 알 필요가 없다.
 * - 스펙: docs/gift-coupon-reselect-soldout-spec.md (백엔드 반영 대기)
 */
const GiftCouponReselectPage: FC = function GiftCouponReselectPage() {
  const { couponId } = useParams<{ couponId: string }>();
  const [searchParams] = useSearchParams();

  /**
   * 관리자 미리보기 — `?secretKey=...` (기존 쿠폰/배송 미리보기와 동일 키).
   * - 상태 게이트(ReselectRequired)를 건너뛰고 실제 쿠폰 데이터로 화면을 그린다
   * - 제출은 서버로 나가지 않고 완료 화면만 로컬로 보여준다 (실제 쿠폰이 바뀌지 않음)
   * - `couponId === 'preview'` 면 쿠폰 조회 없이 샘플 데이터로 그린다 (실데이터 없이 확인용)
   */
  const previewKey = process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY ?? '';
  const isPreview = !!previewKey && searchParams.get('secretKey') === previewKey;
  const isSamplePreview = isPreview && couponId === 'preview';
  /**
   * 발송 전 초안 미리보기 — 관리자가 모달에서 고른 문구·기한·구매자명을 쿼리로 받아 그대로 그린다.
   * 아직 쿠폰에 `reselect`가 저장되기 전이라 이 값이 없으면 기본 문구만 보이기 때문이다.
   */
  const draftMessage = isPreview ? searchParams.get('msg') : null;
  const draftDeadline = isPreview ? searchParams.get('deadline') : null;
  const draftBuyerName = isPreview ? searchParams.get('buyer') : null;

  useGiftCouponByIdHook(couponId || '', !couponId || isSamplePreview);
  const status = useRecoilValue(giftCouponByIdStatusAtomFamily(couponId || ''));
  const fetchedCoupon = useRecoilValue(giftCouponByIdDataAtomFamily(couponId || ''));
  const coupon = isSamplePreview ? PREVIEW_COUPON : fetchedCoupon;

  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // 제출 결과 — 서버 응답을 다시 조회하지 않고 이 값으로 완료 화면을 그린다
  const [doneType, setDoneType] = useState<'reselected' | 'canceled' | null>(null);
  const [doneAt, setDoneAt] = useState<Date | null>(null);

  const giftItem = coupon?.giftItem;
  const giftItemInfo = typeof giftItem === 'object' ? giftItem?.giftItemInfo : null;
  const itemName =
    typeof giftItem === 'object' ? giftItem?.name || giftItemInfo?.name || '' : '';

  const reselect = coupon?.reselect;

  /** 품절된 상품 — reselect 기록 우선, 없으면 현재 선택 상품 */
  const soldOutUnit = useMemo(() => {
    const fromReselect = reselect?.soldOutChoiceUnit;
    if (fromReselect && typeof fromReselect === 'object') return fromReselect;
    const selected = coupon?.selectedChoiceUnit;
    if (selected && typeof selected === 'object') return selected;
    return null;
  }, [reselect, coupon]);

  /**
   * 선택 가능한 상품 — 백엔드가 비활성(enabled=false) 옵션을 걸러 populate 하므로
   * 품절 상품은 원래 목록에 없다. 다만 비활성 처리 전이라면 남아 있을 수 있어 한 번 더 제외한다.
   */
  const choiceUnits = useMemo(() => {
    const units = (giftItemInfo?.choiceUnits ?? []) as GiftChoiceUnitData[];
    return units.filter((u) => u._id !== soldOutUnit?._id);
  }, [giftItemInfo, soldOutUnit]);

  const selectedUnit = useMemo(
    () => choiceUnits.find((u) => u._id === selectedUnitId) ?? null,
    [choiceUnits, selectedUnitId],
  );

  /**
   * 구매자(선물 보낸 분) 이름 — 환불 대상이 누구인지 이름으로 밝혀 준다.
   * `giftOrder.receiverName`(주문자 이름)이며, 구서버 public 응답에는 없을 수 있어 폴백을 둔다.
   */
  const buyerName =
    draftBuyerName?.trim() ||
    (coupon?.giftOrder && typeof coupon.giftOrder === 'object'
      ? coupon.giftOrder.receiverName?.trim() || ''
      : '');
  /** 환불 문장에 쓰는 호칭 — 이름이 있으면 "홍길동님", 없으면 "구매하신 분" */
  const buyerLabel = buyerName ? `${buyerName}님` : '구매하신 분';

  /**
   * 관리자가 고른 안내 사유를 화면에도 간략히 보여준다.
   * 문자 본문에서 말머리(`[...]`)를 떼고 **첫 문단(사유)만** 쓴다 —
   * 뒤의 사과·재선택 안내는 화면이 이미 담고 있어 그대로 붙이면 중복된다.
   */
  const noticeSummary = useMemo(() => {
    const message = draftMessage || reselect?.noticeMessage;
    if (!message) return '';
    const lines = message.split('\n');
    const first = lines[0]?.trim() ?? '';
    const body = first.startsWith('[') && first.endsWith(']') ? lines.slice(1) : lines;
    const paragraph: string[] = [];
    for (const line of body) {
      if (!line.trim()) {
        if (paragraph.length) break; // 첫 문단까지만
        continue;
      }
      paragraph.push(line.trim());
    }
    return paragraph.join(' ').trim();
  }, [reselect, draftMessage]);

  // 미리보기에서 아직 안내를 안 보낸 쿠폰을 열면 기한이 없어 고지 문구가 빠지므로 기본값(+14일)을 채운다
  const deadlineAt = draftDeadline
    ? new Date(draftDeadline)
    : reselect?.deadlineAt
    ? new Date(reselect.deadlineAt)
    : isPreview
    ? new Date(Date.now() + 14 * 86_400_000)
    : null;

  const parseError = (error: unknown, fallback: string) => {
    const axiosError = error as AxiosError<{ message?: string }>;
    if (axiosError.response?.status === 404) {
      return '아직 처리할 수 없는 요청입니다. 잠시 후 다시 시도해 주세요.';
    }
    return axiosError.response?.data?.message || fallback;
  };

  const handleReselect = useCallback(async () => {
    if (!coupon || !selectedUnitId) {
      setSubmitError('받으실 상품을 선택해 주세요.');
      return;
    }
    // 미리보기는 실제 쿠폰을 건드리면 안 되므로 화면만 넘어간다
    if (isPreview) {
      setDoneAt(new Date());
      setDoneType('reselected');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await reselectCoupon(coupon._id, selectedUnitId);
      setDoneAt(new Date());
      setDoneType('reselected');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setSubmitError(parseError(error, '선택에 실패했습니다. 다시 시도해 주세요.'));
    } finally {
      setIsSubmitting(false);
    }
  }, [coupon, selectedUnitId, isPreview]);

  const handleCancelRequest = useCallback(async () => {
    if (!coupon) return;
    if (isPreview) {
      setIsCancelModalOpen(false);
      setDoneType('canceled');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await requestReselectCancel(coupon._id, cancelReason.trim() || undefined);
      setIsCancelModalOpen(false);
      setDoneType('canceled');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setSubmitError(parseError(error, '요청에 실패했습니다. 다시 시도해 주세요.'));
      setIsCancelModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [coupon, cancelReason, isPreview]);

  // --- 렌더링 분기 ---

  if (!isSamplePreview && (status === 'loading' || status === 'idle')) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-50">
        <Loading />
      </div>
    );
  }

  if (!coupon) {
    return (
      <NoticeScreen
        tone="error"
        title="쿠폰을 찾을 수 없습니다"
        message={'유효하지 않은 링크이거나, 이미 처리된 쿠폰입니다.'}
      />
    );
  }

  // 방금 재선택 완료
  if (doneType === 'reselected') {
    const submittedAt = doneAt ?? new Date();
    return (
      <NoticeScreen
        tone="success"
        title="선택이 완료되었습니다"
        message={
          selectedUnit
            ? `${selectedUnit.name}(으)로 준비해 드리겠습니다.`
            : '선택하신 상품으로 준비해 드리겠습니다.'
        }
      >
        <SendScheduleCard
          requestedAt={submittedAt}
          expectedSendAt={getNextCouponSendSlot(submittedAt)}
        />
      </NoticeScreen>
    );
  }

  // 방금 거절 요청 완료 / 이미 접수된 거절 요청
  // (미리보기는 실제 쿠폰 상태와 무관하게 항상 재선택 화면부터 보여준다)
  if (doneType === 'canceled' || (!isPreview && reselect?.status === 'CancelRequested')) {
    return (
      <NoticeScreen
        title="요청이 접수되었습니다"
        message={`선물을 받지 않으시는 것으로 접수되었습니다.\n${buyerLabel}께 결제 금액 전액이 환불될 예정입니다.\n\n처리 전이라면 이 링크에서 다시 상품을 고르실 수 있습니다.`}
      />
    );
  }

  // 재선택이 이미 끝난 링크 (재방문)
  if (!isPreview && reselect?.status === 'Reselected') {
    return (
      <NoticeScreen
        tone="success"
        title="이미 선택이 완료되었습니다"
        message={
          coupon.redeemStatus === GiftCouponRedeemStatus.Delivered
            ? '선택하신 상품이 발송되었습니다.\n문자 또는 알림톡을 확인해 주세요.'
            : '선택하신 상품으로 순차 발송해 드리고 있습니다.'
        }
      />
    );
  }

  // 재선택 대상이 아닌 쿠폰 (안내 철회·취소 처리 등)
  if (!isPreview && coupon.redeemStatus !== GiftCouponRedeemStatus.ReselectRequired) {
    return (
      <NoticeScreen
        title="지금은 이용할 수 없는 링크입니다"
        message={
          '재선택이 필요한 상태가 아닙니다.\n이미 처리되었거나 안내가 취소된 건일 수 있습니다.'
        }
      />
    );
  }

  const noticeImageUrl =
    reselect?.noticeImage && typeof reselect.noticeImage === 'object'
      ? reselect.noticeImage.url
      : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white pb-10">
      {isPreview && (
        <div className="bg-stone-800 px-4 py-2 text-center text-xs font-bold text-white">
          관리자 미리보기 — 실제 쿠폰은 변경되지 않습니다
          {isSamplePreview && ' (샘플 데이터)'}
          {!buyerName && ' · 구매자 이름은 서버 반영 후 표시됩니다'}
        </div>
      )}
      <div className="mx-auto max-w-md px-4 pt-8">
        {/* 품절 안내 */}
        {noticeImageUrl ? (
          <img
            src={fileBucketUrl(noticeImageUrl, 'w800')}
            alt="품절 안내"
            className="w-full rounded-xl border border-stone-200 object-cover"
          />
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-center">
            <ExclamationCircleIcon className="mx-auto h-10 w-10 text-amber-400" />
            <h2 className="mt-3 break-keep text-lg font-bold text-amber-900">
              신청하신 상품이 품절되었습니다
            </h2>
            {/* 사유는 관리자가 고른 안내 문구(사실)를 그대로 보여준다. 문자와 같은 문구가 뜬다.
                (추측성 문구를 쓰지 않기 위해 화면에 원인을 하드코딩하지 않는다) */}
            <p className="mt-1.5 whitespace-pre-line break-keep text-sm leading-relaxed text-amber-700">
              {noticeSummary || '신청해 주신 상품이 품절되었습니다.'}
              <br />
              번거롭게 해 드려 죄송합니다.
            </p>
          </div>
        )}

        {/* 품절된 상품 */}
        {(soldOutUnit || itemName) && (
          <div className="mt-3 flex items-center gap-3 rounded-lg border border-stone-200 bg-white p-3">
            {soldOutUnit?.image?.url && (
              <img
                src={fileBucketUrl(soldOutUnit.image.url, 'w200')}
                alt={soldOutUnit.name}
                className="h-12 w-12 rounded object-cover opacity-40 grayscale"
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-stone-400">
                {buyerName ? `${buyerName}님이 보내신 답례품` : '신청하셨던 상품'}
              </p>
              <p className="truncate text-sm font-medium text-stone-500 line-through">
                {soldOutUnit?.name || itemName}
              </p>
            </div>
            <span className="flex-shrink-0 rounded-full bg-stone-100 px-2.5 py-1 text-xs font-bold text-stone-500">
              품절
            </span>
          </div>
        )}

        {/* 기한 안내 — 전상법상 환급 의무가 있어 항상 날짜로 고지한다 */}
        {deadlineAt && (
          <p className="mt-3 break-keep rounded-lg bg-stone-100 px-3 py-2.5 text-center text-xs font-medium leading-relaxed text-stone-600">
            {formatKoreanDate(deadlineAt)}까지 선택이 없으시면
            <br />
            {buyerLabel}께 전액 환불 처리됩니다.
          </p>
        )}

        {/* 상품 재선택 */}
        <div className="mt-7">
          <h3 className="break-keep text-lg font-bold text-stone-800">
            받으실 상품을 다시 선택해 주세요
          </h3>
          <p className="mt-1 text-sm text-stone-500">
            선택하시면 별도의 인증 없이 바로 접수됩니다.
          </p>

          {choiceUnits.length === 0 ? (
            <div className="mt-3 rounded-lg border border-stone-200 bg-white p-5 text-center text-sm text-stone-500">
              현재 선택 가능한 상품이 준비되어 있지 않습니다.
              <br />
              아래 연락처로 문의해 주시면 안내해 드리겠습니다.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {choiceUnits.map((unit) => (
                <button
                  key={unit._id}
                  onClick={() => {
                    setSelectedUnitId(unit._id);
                    setSubmitError(null);
                  }}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 transition ${
                    selectedUnitId === unit._id
                      ? 'border-stone-700 bg-stone-100'
                      : 'border-stone-200 bg-white'
                  }`}
                >
                  {unit.image?.url && (
                    <img
                      src={fileBucketUrl(unit.image.url, 'w200')}
                      alt={unit.name}
                      className="h-14 w-14 rounded object-cover"
                    />
                  )}
                  <div className="flex-1 text-left">
                    {unit.giftItemBrand?.name && (
                      <span className="mb-0.5 inline-block rounded-full border border-gray-400 bg-white px-2 py-0.5 text-xs font-medium text-stone-600">
                        {unit.giftItemBrand.name}
                      </span>
                    )}
                    <p
                      className={`break-keep font-medium ${
                        selectedUnitId === unit._id ? 'text-stone-900' : 'text-stone-700'
                      }`}
                    >
                      {unit.name}
                    </p>
                  </div>
                  {selectedUnitId === unit._id && (
                    <CheckCircleSolidIcon className="h-7 w-7 text-stone-800" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {submitError && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2.5 text-sm font-medium text-red-600">
            {submitError}
          </p>
        )}

        {choiceUnits.length > 0 && (
          <button
            onClick={handleReselect}
            disabled={isSubmitting || !selectedUnitId}
            className="mt-5 w-full rounded-xl bg-stone-800 py-4 text-base font-bold text-white transition disabled:bg-stone-300"
          >
            {isSubmitting
              ? '처리 중...'
              : selectedUnit
              ? `${selectedUnit.name} 받기`
              : '상품을 선택해 주세요'}
          </button>
        )}

        {/* 거절(구매자 전액 환불) — 안 고르는 것도 정상적인 선택임을 알린다 */}
        <div className="mt-8 border-t border-stone-200 pt-5 text-center">
          <button
            onClick={() => setIsCancelModalOpen(true)}
            disabled={isSubmitting}
            className="break-keep text-sm font-medium text-stone-500 underline underline-offset-4 disabled:opacity-50"
          >
            받고 싶은 상품이 없어요 — {buyerLabel}께 전액 환불해 주세요
          </button>
          <p className="mt-2 break-keep text-xs text-stone-400">
            상품을 고르지 않으셔도 괜찮습니다. {buyerLabel}께 결제 금액 전액이 환불됩니다.
          </p>
        </div>

        <ContactLine className="mt-6" />
      </div>

      {/* 거절 확인 모달 */}
      {isCancelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => !isSubmitting && setIsCancelModalOpen(false)}
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="break-keep text-base font-bold text-stone-800">
              선물을 받지 않으시겠어요?
            </h3>
            <p className="mt-2 break-keep text-sm leading-relaxed text-stone-500">
              {buyerLabel}께 결제 금액 전액이 환불됩니다.
              <br />
              담당자 확인 후 처리되며, 처리 전이라면 이 링크에서 다시 상품을 고르실 수
              있습니다.
            </p>

            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={2}
              placeholder="남기실 말씀이 있으면 적어주세요 (선택)"
              className="mt-3 w-full resize-none rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none"
            />

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setIsCancelModalOpen(false)}
                disabled={isSubmitting}
                className="flex-1 rounded-lg border border-stone-300 py-3 text-sm font-medium text-stone-600 disabled:opacity-50"
              >
                다시 고를게요
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={isSubmitting}
                className="flex-1 rounded-lg bg-stone-800 py-3 text-sm font-bold text-white disabled:opacity-50"
              >
                {isSubmitting ? '처리 중...' : '환불 요청하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftCouponReselectPage;
